import { Chat } from './types';

export interface ChatGroup {
  label: 'Today' | 'Yesterday' | 'Earlier';
  chats: Chat[];
}

// The stored chat title is cut to 30 characters; the history list needs the
// whole prompt, so it reads the first user message instead.
export const firstPrompt = (chat: Chat): string =>
  chat.messages.find((m) => m.role === 'user')?.content ?? '';

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// Calendar arithmetic, not a fixed 24 hours: a DST day is 23 or 25 hours long.
const startOfYesterday = (now: Date): number =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();

/**
 * Chats that have at least one prompt, newest first, filtered by `query` and
 * grouped Today / Yesterday / Earlier by when they were last updated.
 * Empty groups are left out.
 */
export const groupChats = (chats: Chat[], query: string, now: Date = new Date()): ChatGroup[] => {
  const needle = query.trim().toLowerCase();
  const today = startOfDay(now);
  const yesterday = startOfYesterday(now);

  const groups: ChatGroup[] = [
    { label: 'Today', chats: [] },
    { label: 'Yesterday', chats: [] },
    { label: 'Earlier', chats: [] }
  ];

  chats
    .filter((chat) => {
      const prompt = firstPrompt(chat);
      return prompt !== '' && prompt.toLowerCase().includes(needle);
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .forEach((chat) => {
      const day = startOfDay(new Date(chat.updatedAt));
      const index = day >= today ? 0 : day >= yesterday ? 1 : 2;
      groups[index].chats.push(chat);
    });

  return groups.filter((group) => group.chats.length > 0);
};

export const formatChatTime = (value: Date | string, now: Date = new Date()): string => {
  const date = new Date(value);
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (startOfDay(date) >= startOfYesterday(now)) return time;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};
